import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexStroke,
  ApexXAxis,
  ApexFill,
  ApexLegend,
  ApexTooltip,
  ApexYAxis,
} from 'ng-apexcharts';
import {
  ReportInProgressCompanies,
  ReportNewsCompanies,
} from '@core/models/statistics-report.model';
import { getPeriod } from '@utils/get-period';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  stroke: ApexStroke;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  fill: ApexFill;
  legend: ApexLegend;
  tooltip: ApexTooltip;
};

@Component({
  selector: 'app-companies-statistics',
  templateUrl: './companies-statistics.component.html',
  styleUrls: ['./companies-statistics.component.scss'],
})
export class CompaniesStatisticsComponent implements OnChanges {
  @Input() statisticsNewCompanies: ReportNewsCompanies[] = [];
  @Input() statisticsCompaniesProgress: ReportInProgressCompanies[] = [];
  @Input() informationDay = '';

  chartOptions: Partial<ChartOptions> = this.createInitialOptions();
  hasData = false;

  // -------------------------
  private static getDaysOfMonth(date?: Date): string[] {
    const targetDate = date ?? new Date();
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    return Array.from({ length: daysInMonth }, (_, i) => `${i + 1}`);
  }

  // -------------------------
  // INITIAL CONFIG

  ngOnChanges(changes: SimpleChanges): void {
    const hasNews = this.statisticsNewCompanies?.length > 0;
    const hasProgress = this.statisticsCompaniesProgress?.length > 0;

    this.hasData = hasNews || hasProgress;

    if (
      this.hasData &&
      (changes.statisticsNewCompanies ||
        changes.statisticsCompaniesProgress ||
        changes.informationDay)
    ) {
      Promise.resolve().then(() => this.refreshChart());
    }
  }

  private createInitialOptions(): Partial<ChartOptions> {
    return {
      series: [],
      chart: {
        type: 'area',
        stacked: true,
        height: 350,
        toolbar: { show: false },
      },
      dataLabels: {
        enabled: true,
        formatter: (val: number) => Math.round(val).toString(),
      },
      stroke: { curve: 'smooth' },
      fill: {
        type: 'gradient',
        gradient: {
          opacityFrom: 0.6,
          opacityTo: 0.8,
        },
      },
      xaxis: {
        categories: CompaniesStatisticsComponent.getDaysOfMonth(),
        decimalsInFloat: 0,
      },
      yaxis: {
        show: false,
      },

      legend: { position: 'top' },

      tooltip: {
        shared: true,
        intersect: false,
        x: {
          formatter: (val: number) => `Day ${val}`,
        },
        y: {
          formatter: (val: number) => Math.round(val).toString() + ' companies',
        },
      },
    };
  }

  // -------------------------
  // UPDATE CHART
  // -------------------------
  private refreshChart(): void {
    const news = [...this.statisticsNewCompanies].sort((a, b) => a.day - b.day);

    const progress = [...this.statisticsCompaniesProgress].sort(
      (a, b) => a.day - b.day,
    );

    if (this.informationDay === getPeriod()) {
      this.chartOptions = {
        ...this.chartOptions,
        series: [
          {
            name: 'News Companies',
            data: news.map((s) => s.totalCompanies),
          },
          {
            name: 'Company in Progress',
            data: progress.map((s) => s.totalCompanies),
          },
        ],
        legend: {
          position: 'top',
          showForSingleSeries: true,
        },
        dataLabels: {
          enabled: true,
        },
      };
    } else {
      this.chartOptions = {
        ...this.chartOptions,
        series: [
          {
            name: 'News Companies',
            data: news.map((s) => s.totalCompanies),
          },
        ],
      };
    }
  }
}
